import { useState, useEffect } from 'react';
import axios from 'axios';
import { Target, TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Activation Funnel widget for the Admin Dashboard.
 * Shows: % of cleaners hitting the BG-check-waiver threshold + a recommendation
 * to tighten / loosen / hold the threshold.
 */
const ActivationFunnelWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios
      .get(`${API}/admin/activation-funnel`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Failed to load activation data'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="bg-surface rounded-2xl p-6 border border-border flex items-center gap-3" data-testid="activation-funnel-loading">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
        <span className="text-sm text-text-secondary">Loading activation funnel…</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-surface rounded-2xl p-6 border border-border text-sm text-text-secondary" data-testid="activation-funnel-error">
        Activation funnel: {error || 'no data'}
      </div>
    );
  }

  if (!data.settings.waiver_enabled || data.settings.payer !== 'platform_recoup') {
    return (
      <div className="bg-surface rounded-2xl p-6 border border-border" data-testid="activation-funnel-disabled">
        <div className="flex items-center gap-2 mb-2">
          <Target className="w-5 h-5 text-text-secondary" />
          <h3 className="font-heading text-lg font-semibold text-text-primary">Activation Funnel</h3>
        </div>
        <p className="text-sm text-text-secondary">
          Enable <strong>Platform pays + recoups</strong> with the activation waiver in Settings to see this funnel.
        </p>
      </div>
    );
  }

  const rate = data.activation_rate;
  const ratePct = rate == null ? '—' : `${(rate * 100).toFixed(0)}%`;
  const { waived, missed, in_progress, completed_cohort, total_cleaners } = data.totals;
  const { total_fees_waived, total_fees_recouped } = data.cost_impact;

  const action = data.recommendation.action;
  const actionConfig = {
    loosen: { Icon: TrendingDown, bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-900', label: 'Suggest LOOSEN' },
    tighten: { Icon: TrendingUp, bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-900', label: 'Suggest TIGHTEN' },
    hold: { Icon: Minus, bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-900', label: 'HOLD steady' },
  }[action];
  const ActionIcon = actionConfig.Icon;

  // Donut math: 3 slices proportional to waived / missed / in_progress
  const total = waived + missed + in_progress || 1;
  const waivedPct = (waived / total) * 100;
  const missedPct = (missed / total) * 100;

  return (
    <div className="bg-surface rounded-2xl p-6 border border-border" data-testid="activation-funnel-widget">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-lg font-semibold text-text-primary">Activation Funnel</h3>
          </div>
          <p className="text-xs text-text-secondary">
            % of cleaners hitting <strong>{data.settings.bookings_required} bookings in {data.settings.days_window} days</strong> → BG-check fee waived
          </p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${actionConfig.bg} ${actionConfig.border} border ${actionConfig.text}`} data-testid="activation-recommendation-badge">
          <ActionIcon className="w-3.5 h-3.5" />
          {actionConfig.label}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 items-center">
        {/* Big rate */}
        <div className="lg:col-span-2 text-center" data-testid="activation-rate-display">
          <p className="text-6xl font-heading font-bold text-primary">{ratePct}</p>
          <p className="text-xs text-text-secondary mt-1">
            activation rate ({waived} of {completed_cohort} completed cohort)
          </p>
        </div>

        {/* Breakdown bar */}
        <div className="lg:col-span-3 space-y-3">
          <div className="flex h-3 rounded-full overflow-hidden bg-surface-muted">
            {waived > 0 && <div className="bg-emerald-500" style={{ width: `${waivedPct}%` }} title={`${waived} waived`} />}
            {missed > 0 && <div className="bg-rose-400" style={{ width: `${missedPct}%` }} title={`${missed} missed`} />}
            {in_progress > 0 && <div className="bg-amber-300" style={{ width: `${100 - waivedPct - missedPct}%` }} title={`${in_progress} in progress`} />}
          </div>
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div className="flex items-center gap-2" data-testid="funnel-waived">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
              <div>
                <p className="font-semibold text-text-primary">{waived} waived</p>
                <p className="text-text-secondary">${total_fees_waived.toFixed(0)} given up</p>
              </div>
            </div>
            <div className="flex items-center gap-2" data-testid="funnel-missed">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
              <div>
                <p className="font-semibold text-text-primary">{missed} missed</p>
                <p className="text-text-secondary">${total_fees_recouped.toFixed(0)} recouped</p>
              </div>
            </div>
            <div className="flex items-center gap-2" data-testid="funnel-in-progress">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
              <div>
                <p className="font-semibold text-text-primary">{in_progress} in window</p>
                <p className="text-text-secondary">still tracking</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendation card */}
      <div className={`mt-5 p-4 rounded-xl border ${actionConfig.bg} ${actionConfig.border}`} data-testid="activation-recommendation">
        <p className={`text-sm ${actionConfig.text}`}>{data.recommendation.text}</p>
      </div>

      <p className="text-xs text-text-secondary mt-3">
        Cohort: {total_cleaners} total cleaners · {completed_cohort} with window closed · ${data.settings.fee.toFixed(2)} fee per check
      </p>
    </div>
  );
};

export default ActivationFunnelWidget;
